import { brandPalette } from "../../../../constants/brandPalette";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  SimpleGrid,
  Stack,
  Text,
  VStack,
  Image,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { toast } from "react-toastify";
import MainInput from "../../../../components/Form/input";
import MainModel from "../../../../components/Models/main-model";
import {
  createChapterRequest,
  editChapterRequest,
} from "../../../../modules/admin-modules/chapters/Actions";
import { closeCreateChapterModel } from "../../../../modules/admin-modules/models/Actions";
import { getAllAdminSectionsRequest } from "../../../../modules/admin-modules/sections/Actions";
import { getPaymentSubjectRequest } from "../../../../modules/admin-modules/videos/Actions";
import {
  adminModalBodyProps,
  adminModalContentProps,
  adminModalHeaderProps,
  adminSelectStyles,
  withAdminSelectMenuPortal,
} from "../../../../components/Models/admin-modal-theme";

const selectStyles = withAdminSelectMenuPortal(adminSelectStyles());
const selectPortalProps = {
  menuPortalTarget: typeof document !== "undefined" ? document.body : null,
  menuPosition: "fixed",
  maxMenuHeight: 240,
};

const AddChapter = ({ isAdd, data, setSelectedData, defaultActive, image = true }) => {
  const [fileLabel, setFileLabel] = useState("");
  const openChapter = useSelector((state) => state.model.isOpen11);
  const isLoading = useSelector((state) => state.chapters.isLoading);
  const sections = useSelector((state) => state.adminSections.sections);
  const paymentSubjects = useSelector((state) => state.videos.paymentSubjects);

  const sectionsOptions = sections?.map((s) => ({ value: s.id, label: s.name }));
  const sectionDefault = sectionsOptions?.find((o) => o.value === data?.section_id);
  const paymentSubjectsOptions = paymentSubjects?.map((p) => ({ value: p.id, label: p.name }));
  const paymentSubjectDefault = data?.payment_subject ? { value: data?.payment_subject?.id, label: data?.payment_subject?.name } : undefined;

  const dispatch = useDispatch();
  useEffect(() => {
    if (openChapter) {
      dispatch(getAllAdminSectionsRequest());
      dispatch(getPaymentSubjectRequest());
    }
  }, [openChapter, dispatch]);

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      name: data?.name || "",
      name_ar: data?.name_ar || "",
      section_id: data?.section_id,
    },
    shouldUnregister: true,
  });

  useEffect(() => {
    if (openChapter && sectionDefault?.value != null) {
      setValue("section_id", sectionDefault.value);
    }
  }, [openChapter, sectionDefault, setValue]);

  const header = (
    <Box>
      <Heading
        fontSize="28px"
        fontWeight="800"
        color="var(--text-primary)"
        fontFamily="'Amiri','Tajawal',sans-serif"
        textAlign="center"
      >
        {defaultActive === 0
          ? isAdd
            ? "Add question container"
            : "Edit question container"
          : isAdd
            ? "Add lesson"
            : "Edit lesson"}
      </Heading>
      <Text
        fontSize="xs"
        mt={1}
        color="var(--text-muted)"
        textTransform="uppercase"
        letterSpacing="0.12em"
        fontWeight="700"
        textAlign="center"
      >
        {defaultActive === 0
          ? "Names, course"
          : "Names, course, and optional image"}
      </Text>
    </Box>
  );

  const onSubmit = async (values) => {
    const action = () => {
      dispatch(closeCreateChapterModel());
      setSelectedData({});
      reset();
      setFileLabel("");
      if (isAdd) {
        window.location.reload();
      }
    };

    if (isAdd) {
      const merged = { ...getValues(), ...values };
      const sectionId = merged.section_id;
      if (sectionId == null || sectionId === "") {
        toast.error("Please select a section.");
        return;
      }
      merged.section_id = sectionId;
      const formData = new FormData();
      for (const key of Object.keys(merged)) {
        const v = merged[key];
        if (v === undefined || v === null) continue;
        formData.append(key, v);
      }
      formData.append("name_ar", values?.name);
      if (defaultActive === 0) {
        formData.set("active", "0");
      }
      await dispatch(createChapterRequest(formData, action));
    } else {
      const formData = new FormData();
      formData.append("_method", "PUT");
      const total = { ...data, ...values };
      const skip = new Set(["section", "subject", "grade", "lessons", "created_at", "updated_at"]);
      for (const key of Object.keys(total)) {
        if (skip.has(key)) continue;
        const v = total[key];
        if (v === undefined || v === null) continue;
        if (typeof v === "object" && !(v instanceof File) && !Array.isArray(v)) continue;
        formData.append(key, v);
      }
      formData.append("name_ar", values?.name);
      if (defaultActive === 0) {
        formData.set("active", "0");
      }
      dispatch(
        editChapterRequest({
          id: data?.id,
          formData,
          action,
        })
      );
    }
  };

  const closeFunc = () => {
    dispatch(closeCreateChapterModel());
    setSelectedData({});
    reset();
    setFileLabel("");
  };

  const baseUrl = 'https://api.edutopiaonline.com/public/';

  return (
    <MainModel
      isLoading={isLoading}
      header={header}
      closeFunc={closeFunc}
      openVar={openChapter}
      buttonTitle={null}
      buttonFunc={handleSubmit(onSubmit)}
      size="xl"
      hideDefaultFooter
      contentProps={adminModalContentProps}
      headerProps={adminModalHeaderProps}
      bodyProps={adminModalBodyProps}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={6} dir="ltr" w="100%">
          <SimpleGrid columns={{ base: "auto", md: "1 / -1" }} spacingX={8} spacingY={6} alignItems="start">
            <MainInput
              label="Name (English)"
              defaultValues={data?.name}
              error={errors?.name?.message}
              placeholder="Lesson title in English"
              register={register}
              name="name"
            />

            {image && (
              <FormControl gridColumn={{ base: "auto", md: "1 / -1" }}>
              <VStack align="stretch" spacing={2}>
                <FormLabel m={0} fontSize="14px" fontWeight="700" color="var(--text-primary)" textAlign="left">
                  Cover image
                </FormLabel>
                <Button
                  bg={brandPalette.primary}
                  color="white"
                  borderRadius="lg"
                  position="relative"
                  h="42px"
                  fontWeight="700"
                  _hover={{ bg: brandPalette.accentDark }}
                >
                  <Text>{fileLabel || "Choose file to upload"}</Text>
                  <Input
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) {
                        setValue("logo", f);
                        setFileLabel(f.name);
                      }
                    }}
                    type="file"
                    accept="image/*"
                    opacity="0"
                    position="absolute"
                    w="100%"
                    h="100%"
                    left={0}
                    top={0}
                    cursor="pointer"
                  />
                </Button>
              </VStack>
            </FormControl>
            )}

            {data?.logo && (
              <Image src={baseUrl + data?.logo} alt="Chapter logo" />
            )}

            <Box gridColumn={{ base: "auto", md: "1 / -1" }}>
              <FormControl>
                <VStack align="stretch" spacing={2}>
                  <FormLabel m={0} fontSize="14px" fontWeight="700" color="var(--text-primary)" textAlign="left">
                    Course
                  </FormLabel>
                  <Select
                    inputId="chapter_section_id"
                    options={sectionsOptions}
                    defaultValue={sectionDefault}
                    onChange={(opt) =>
                      setValue("section_id", opt?.value, { shouldValidate: true, shouldDirty: true })
                    }
                    styles={selectStyles}
                    placeholder="Select course"
                    {...selectPortalProps}
                  />
                </VStack>
              </FormControl>
            </Box>


            {image && (
              <Box gridColumn={{ base: "auto", md: "1 / -1" }}>
              <FormControl>
                <VStack align="stretch" spacing={2}>
                  <FormLabel m={0} fontSize="14px" fontWeight="700" color="var(--text-primary)" textAlign="left">
                    Payment
                  </FormLabel>
                  <Select
                    inputId="chapter_payment_subject_id"
                    options={paymentSubjectsOptions}
                    defaultValue={paymentSubjectDefault}
                    onChange={(opt) => setValue("payment_subject_id", opt?.value, { shouldValidate: true, shouldDirty: true })}
                    styles={selectStyles}
                    placeholder="Select payment"
                    isLoading={!paymentSubjectsOptions}
                    {...selectPortalProps}
                  />
                </VStack>
              </FormControl>
            </Box>
            )}
          </SimpleGrid>

          <Flex justify="flex-end" gap={6} pt={2}>
            <Button
              variant="ghost"
              color="var(--text-primary)"
              fontWeight="800"
              _hover={{ textDecoration: "underline", bg: "rgba(91, 13, 22, 0.08)" }}
              onClick={closeFunc}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              bg={brandPalette.primary}
              color="white"
              borderRadius="lg"
              px={8}
              py={6}
              fontWeight="800"
              isLoading={isLoading}
              _hover={{ bg: brandPalette.accentDark }}
              boxShadow="0 10px 24px rgba(91, 13, 22, 0.18)"
            >
              Save changes
            </Button>
          </Flex>
        </Stack>
      </form>
    </MainModel>
  );
};

export default AddChapter;
